import $ from 'jquery';
import { ISearchResult } from './interface';
import Axios from 'axios';
import { Events } from '../../shared/eventbus/events';
import { publish, subscribe } from '../../shared/eventbus/eventbus';

const templateRecommendation = require('./view/recommendation.handlebars');
const templateProducts = require('./view/products.handlebars');
const templateWiki = require('./view/wiki.handlebars');
const templateForum = require('./view/forum.handlebars');
const templateGamecloud = require('./view/gamecloud/gamecloud.handlebars');

const MIN_INPUT_LENGTH = 2;

const NAVIGATION_SEARCH_FIELD_SELECTOR = '[data-searchfield]';
const NAVIGATION_SEARCH_CLOSE_SELECTOR = '[data-close-search]';
const SEARCH_SELECTOR = '[data-search]';
const NAVIGATION_SEARCH_IS_OPEN_CLASSNAME = 'is-open';
const NAVIGATION_SEARCH_RECOMMANDATION_SELECTOR = '.recommendations';
const NAVIGATION_SEARCH_PRODUCTS_SELECTOR = '.products';
const NAVIGATION_SEARCH_WIKI_SELECTOR = '.wiki';
const NAVIGATION_SEARCH_FORUM_SELECTOR = '.forum';
const NAVIGATION_GAMECLOUD_MENU_SELECTOR = '[data-gamecloud-menu]';

/**
 * Navigation UI Compoentn
 * Handle UI Actions for the navigation
 */

export class SearchUI {
    private timeout;

    public GamecloudData: any = null;

    /**
     * Constuctor
     * @param  $base - jQuery element of the navigation DOM  base
     */
    constructor(private $base: JQuery) { }

    /**
     * findMenuItemByURL
     * find a dom node with a href
     * @param url string of the current url
     */
    public findMenuItemByURL(url: string): JQuery {
      const menuElement: JQuery = $('.navigation__main-menu');

      // First search exact match for best performance
      const link: JQuery = menuElement
        .find(`.navigation__menu__link[href="${url}"]`)
        .filter(':not([data-ignore-menuitem])')
        .first();

      if (link.length) {
        return link;
      }

      // Search all other possibly matching links
      const links: JQuery = menuElement
        .find('.navigation__menu__link[href]')
        .filter(':not([data-ignore-menuitem])')
        .filter(function (index: number, element: HTMLElement): boolean {
          return url.startsWith($(this).attr('href'));
        });

      if (!links.length) {
        return undefined;
      }

      // Reverse sort URLs by length (we want to find the longest matching URL)
      const urls = jQuery.map(links, (element: HTMLElement, index: number): string => $(element).attr('href'));
      urls.sort((a: string, b: string): number => b.length - a.length);
      url = urls[0];

      return links
        .filter(`[href="${url}"]`)
        .first();
    }

    /**
     * The user wants to hide the search
     */

    public onSearchHideClick() {
      return new Promise<void>((resolve) => {
        $(NAVIGATION_SEARCH_CLOSE_SELECTOR).off('click').on('click', () => {
          this.hideSearch().then(() => {
            resolve();
          });
        });
      });
    }

    public onWindowSearchOpen() {
      subscribe(Events.OpenSearchOverlay, () => this.showSearch());
    }

    /**
     * onSearchInput
     * The user enter a text
     */
    public onSearchInput(callback) {
      document.querySelectorAll(NAVIGATION_SEARCH_FIELD_SELECTOR)[0].addEventListener('keyup', (event: Event) => {
        clearTimeout(this.timeout);
        const element = event.target as HTMLInputElement;
        if (element.value.length >= MIN_INPUT_LENGTH) {
          this.timeout = window.setTimeout(() => {
            callback(element.value);
          }, 500);
        }
      });
    }

    /**
     * Render
     * Update view
     */
    public render(searchResult?: ISearchResult) {
      this.renderMyServerMenu();
      if (searchResult && typeof searchResult !== 'undefined') {
        this.renderRecommendation(searchResult.helpfulLinks);
        this.renderProducts(searchResult.product);
        this.renderWiki(searchResult.wiki);
        this.renderForum(searchResult.forum);
      }
    }

    /**
     * Render lists of recommendations
     */
    private renderRecommendation(templateData) {
      this.$base.find(NAVIGATION_SEARCH_RECOMMANDATION_SELECTOR).html(templateRecommendation({ data: templateData }));
    }

    /**
     * Render lists of products
     */
    private renderProducts(templateData) {
      let show = false;
      let empty = true;
      if (typeof templateData !== 'undefined') {
        show = true;

        if (templateData.length > 0) {
          empty = false;
        }
      }

      this.$base.find(NAVIGATION_SEARCH_PRODUCTS_SELECTOR).html(templateProducts({
        data: templateData,
        show,
        empty,
      }));
    }

    /**
     * Render lists of forum entries
     */
    private renderForum(templateData) {
      let show = false;
      let empty = true;
      if (typeof templateData !== 'undefined') {
        show = true;

        if (templateData.length > 0) {
          empty = false;
        }
      }

      this.$base.find(NAVIGATION_SEARCH_FORUM_SELECTOR).html(templateForum({
        data: templateData,
        show,
        empty,
      }));
    }

    /**
     * Render lists of wiki entries
     */
    private renderWiki(templateData) {
      let show = false;
      let empty = true;
      if (typeof templateData !== 'undefined') {
        show = true;

        if (templateData.length > 0) {
          empty = false;
        }
      }

      this.$base.find(NAVIGATION_SEARCH_WIKI_SELECTOR).html(templateWiki({
        data: templateData,
        show,
        empty,
      }));
    }

    private renderMyServerMenu() {
      let noServers = false;

      if (this.GamecloudData && typeof this.GamecloudData !== 'undefined' && this.GamecloudData.length === 0) {
        noServers = true;
      }

      $(NAVIGATION_GAMECLOUD_MENU_SELECTOR).html(templateGamecloud({
        data: this.GamecloudData,
        noServers,
        homepageUrl: window.homepageUrl,
        regionPrefix: window.regionPrefix,
        localePrefix: window.localePrefix,
      }));
    }

    /**
     * Show search
     * Add a class to the search base
     */
    private showSearch() {
      // Lazily fetch default results when search is shown, not on page load.
      // $(NAVIGATION_SEARCH_FIELD_SELECTOR).keyup();
      const url: string = window.location.hostname.includes('g-portal.us') ? 'https://search.g-portal.us/' : 'https://search.g-portal.com/';
      this.loading(true);
      Axios.get(url).then((response) => {
        this.render(response.data);
        this.loading(false);
      });

      return new Promise<void>((resolve) => {
        this.$base.addClass(NAVIGATION_SEARCH_IS_OPEN_CLASSNAME);
        resolve();
      });
    }

    /**
     * Hide search
     * Remove class from the search base
     */

    private hideSearch() {
      return new Promise<void>((resolve) => {
        this.$base.removeClass(NAVIGATION_SEARCH_IS_OPEN_CLASSNAME);
        publish(Events.HideSearchOverlay, {});
        resolve();
      });
    }

    public loading(state: boolean) {
      if (state) {
        $('.search__input__loader').addClass('is-visible');
      } else {
        $('.search__input__loader').removeClass('is-visible');
      }
    }
}

/**
 * createNavigationUi
 * Create a array of NavigationUI instances
 * @return [NavigationUI]
 */
export function createSearchUI(): SearchUI[] {
  const $base = $(SEARCH_SELECTOR);
  const instances: SearchUI[] = [];

  $base.each((index, el) => {
    instances.push(new SearchUI($base));
  });

  return instances;
}
